import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';
import {
  FACET_RENDER_SUPERSEDE_EVENT,
  createSectionRenderAuthority,
  getPageCacheKey,
  isSectionRenderAuthorityCurrent,
  normalizeHistoryURL,
  normalizeRequestURL,
  settleSectionRenderAuthority,
} from '@theme/paginated-list';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Product-only search query parameter.
 * @type {string}
 */
const SEARCH_TYPE = 'type';

/**
 * Product-only search query value.
 * @type {string}
 */
const PRODUCT_SEARCH_TYPE = 'product';

const FACET_RENDER_SETTLED_EVENT = 'facet:section-render-settled';
const FACET_DOM_MUTATION_EVENT = 'facet:section-dom-mutation';

let renderTransactionSequence = 0;

/** @type {Map<string, string>} */
const latestRenderTokens = new Map();

/**
 * Runs product-grid work only while the exact operation owner remains authoritative. Cancellation
 * is handled by the utility operation control, so a stale transition never serially blocks a newer
 * Facets operation on its arbitrarily delayed `finished` promise.
 *
 * @param {{ isCurrent: () => boolean }} owner
 * @param {() => Promise<void>} task
 */
async function runProductGridTransition(owner, task) {
  if (!owner.isCurrent()) return;
  try {
    await task();
  } catch (error) {
    if (owner.isCurrent()) throw error;
  }
}

/**
 * @typedef {Object} FacetRenderTransaction
 * @property {string} token
 * @property {string} sectionId
 * @property {string} requestURL
 * @property {string} historyURL
 * @property {string} urlParameters
 * @property {'filter' | 'popstate'} origin
 * @property {'write' | 'none'} historyPolicy
 * @property {boolean} publishHistory
 * @property {boolean} historyPublished
 * @property {number} [generation]
 * @property {boolean} [morphed]
 * @property {'pending' | 'success' | 'failure' | 'superseded'} settlement
 * @property {ReturnType<typeof createSectionRenderAuthority>} [authority]
 * @property {boolean} facetOwned
 * @property {AbortController} transitionController
 * @property {{
 *   sectionId: string,
 *   transactionId: string,
 *   generation: number,
 *   navigationEpoch: number,
 *   browserEntry: object,
 *   requestTarget: string,
 *   domMutationLeaseId: string | undefined,
 *   root: ParentNode,
 *   signal: AbortSignal,
 *   isCurrent: () => boolean
 * }} [transitionOwner]
 * @property {boolean} [mutationStarted]
 * @property {ReturnType<typeof sectionRenderer.createSectionRender>} [preparedRender]
 */

/**
 * Records section HTML that the canonical renderer can reuse.
 * @param {string} sectionId
 * @param {URL | string} url
 */
async function prefetchSection(sectionId, url) {
  const requestURL = normalizeRequestURL(url);

  try {
    await sectionRenderer.getSectionHTML(sectionId, true, new URL(requestURL.toString()));
  } catch (error) {
    if (typeof error !== 'object' || error === null || !('name' in error) || error.name !== 'AbortError') {
      console.error(error);
    }
  }
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  #instanceToken = Symbol('facets-form-instance');

  /** @type {Array<{ element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, checked?: boolean, value: string, disabled: boolean, selectedValues?: string[] }>} */
  #committedControlState = [];

  /** @type {Array<{ element: HTMLElement, html: string, className: string }>} */
  #committedStatusState = [];

  /** @type {string | undefined} */
  #activeBusyToken;

  /** @type {string | null} */
  #committedAriaBusy = null;

  /** @type {FacetRenderTransaction | undefined} */
  #activeRenderTransaction;

  /** @type {Set<string>} */
  #settledRenderTokens = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#captureCommittedUI();
    document.addEventListener(FACET_RENDER_SUPERSEDE_EVENT, this.#handleRenderSupersede);
  }

  disconnectedCallback() {
    this.#activeRenderTransaction?.transitionController.abort();
    super.disconnectedCallback();
    document.removeEventListener(FACET_RENDER_SUPERSEDE_EVENT, this.#handleRenderSupersede);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#captureCommittedUI();
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    if (this.#isSearchPage()) newParameters.set(SEARCH_TYPE, PRODUCT_SEARCH_TYPE);

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  /**
   * Checks whether the current URL is the locale-aware search route.
   * @returns {boolean}
   */
  #isSearchPage() {
    const searchUrl = new URL(Theme.routes.search_url, window.location.origin);
    return window.location.pathname === searchUrl.pathname;
  }

  /**
   * Adds the product-only contract to locale-aware search URLs.
   * @param {string} url
   * @returns {string}
   */
  #normalizeSearchURL(url) {
    const normalizedUrl = normalizeHistoryURL(new URL(url, window.location.origin));
    const searchUrl = new URL(Theme.routes.search_url, window.location.origin);

    if (normalizedUrl.pathname === searchUrl.pathname) {
      const searchQuery = normalizedUrl.searchParams.get(SEARCH_QUERY);
      if (searchQuery !== null) normalizedUrl.searchParams.set(SEARCH_QUERY, searchQuery);
      normalizedUrl.searchParams.set(SEARCH_TYPE, PRODUCT_SEARCH_TYPE);
      normalizedUrl.searchParams.sort();
    }

    return normalizedUrl.toString();
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Builds the browser and request URLs without publishing an optimistic history entry.
   *
   * @param {URLSearchParams} urlParameters
   * @returns {{ historyURL: URL, requestURL: URL }}
   */
  #buildFilterURLs(urlParameters) {
    const url = new URL(window.location.href);

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    const historyURL = normalizeHistoryURL(url);
    return {
      historyURL,
      requestURL: normalizeRequestURL(historyURL),
    };
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    const urlParameters = this.createURLParameters();
    const { historyURL, requestURL } = this.#buildFilterURLs(urlParameters);
    this.#startFilterRender(urlParameters, historyURL, requestURL);
  };

  /**
   * Starts one authoritative render transaction.
   * @param {URLSearchParams} urlParameters
   * @param {URL} historyURL
   * @param {URL} requestURL
   */
  #startFilterRender(urlParameters, historyURL, requestURL) {
    const sectionId = this.sectionId;
    const requestKey = getPageCacheKey(requestURL);
    const targetHistoryURL = normalizeHistoryURL(historyURL).toString();
    const activeTransaction = this.#activeRenderTransaction;
    if (
      activeTransaction &&
      this.#canAdoptActiveTransaction(activeTransaction, requestKey, targetHistoryURL)
    ) {
      return;
    }
    if (activeTransaction) this.#finishSupersededTransaction(activeTransaction, 'network');

    const transaction = /** @type {FacetRenderTransaction} */ ({
      token: `${sectionId}:${++renderTransactionSequence}`,
      sectionId,
      requestURL: requestKey,
      historyURL: targetHistoryURL,
      urlParameters: urlParameters.toString(),
      origin: 'filter',
      historyPolicy: 'write',
      publishHistory: true,
      historyPublished: false,
      settlement: 'pending',
      facetOwned: true,
      transitionController: new AbortController(),
    });

    latestRenderTokens.set(sectionId, transaction.token);
    this.#activeRenderTransaction = transaction;
    this.#setTransactionBusy(transaction);

    const filterUpdateEvent = new FilterUpdateEvent(urlParameters);
    filterUpdateEvent.detail.renderTransaction = transaction;
    this.dispatchEvent(filterUpdateEvent);

    transaction.authority ??= createSectionRenderAuthority(transaction, {
      instanceToken: this.#instanceToken,
    });
    const authority = transaction.authority;
    const authoritySnapshot = authority.snapshot;
    transaction.transitionOwner = Object.freeze({
      sectionId,
      transactionId: transaction.token,
      generation: authoritySnapshot.generation,
      navigationEpoch: authoritySnapshot.navigationEpoch,
      browserEntry: authoritySnapshot.browserEntry,
      requestTarget: authoritySnapshot.requestKey,
      domMutationLeaseId: authoritySnapshot.domMutationLeaseId,
      root: this.closest('.shopify-section') ?? document,
      signal: transaction.transitionController.signal,
      isCurrent: () =>
        transaction.authority === authority &&
        transaction.transitionOwner?.generation === authoritySnapshot.generation &&
        transaction.transitionOwner?.navigationEpoch === authoritySnapshot.navigationEpoch &&
        transaction.transitionOwner?.browserEntry === authoritySnapshot.browserEntry &&
        transaction.transitionOwner?.requestTarget === authoritySnapshot.requestKey &&
        transaction.transitionOwner?.domMutationLeaseId === authoritySnapshot.domMutationLeaseId &&
        this.#isTransactionCurrent(transaction),
    });
    void this.#updateSection(transaction);
  }

  /**
   * Preserves active request adoption only for the exact immutable operation authority.
   *
   * @param {FacetRenderTransaction} transaction
   * @param {string} requestKey
   * @param {string} historyURL
   * @returns {boolean}
   */
  #canAdoptActiveTransaction(transaction, requestKey, historyURL) {
    return Boolean(
      transaction.requestURL === requestKey &&
        transaction.historyURL === historyURL &&
        transaction.authority &&
        transaction.transitionOwner &&
        !transaction.transitionController.signal.aborted &&
        this.#isTransactionCurrent(transaction)
    );
  }

  /**
   * @param {FacetRenderTransaction} transaction
   * @returns {boolean}
   */
  #isTransactionCurrent(transaction) {
    return (
      latestRenderTokens.get(transaction.sectionId) === transaction.token &&
      Boolean(transaction.authority) &&
      isSectionRenderAuthorityCurrent(transaction.authority) &&
      (transaction.settlement === 'pending' || transaction.settlement === 'success')
    );
  }

  /**
   * Resolves HTML before the transition and allows the guarded morph only in its update callback.
   * Every authoritative attempt publishes one terminal settlement.
   *
   * @param {FacetRenderTransaction} transaction
   */
  async #updateSection(transaction) {
    const useViewTransition = !this.closest('dialog');
    const requestURL = normalizeRequestURL(transaction.requestURL);
    const transitionOwner = transaction.transitionOwner;

    if (!transitionOwner || !this.#isTransactionCurrent(transaction)) {
      this.#finishSupersededTransaction(transaction, 'network');
      return;
    }

    const render = sectionRenderer.createSectionRender(transaction.sectionId, {
      cache: true,
      url: new URL(requestURL.toString()),
    });
    transaction.preparedRender = render;

    try {
      const prepared = await render.ready;

      if (!this.#isTransactionCurrent(transaction)) {
        render.abort();
        this.#finishSupersededTransaction(transaction, prepared.source);
        return;
      }

      /** @type {{ status: 'success' | 'superseded', html: string, source: 'cache' | 'network', requestURL: string } | undefined} */
      let outcome;
      /** @type {unknown} */
      let morphError;
      /** @type {Promise<void> | undefined} */
      let commitPromise;

      const commit = () => {
        if (!this.#isTransactionCurrent(transaction)) {
          render.abort();
          outcome = {
            status: 'superseded',
            html: prepared.html,
            source: prepared.source,
            requestURL: transaction.requestURL,
          };
          return Promise.resolve();
        }

        if (!transaction.mutationStarted) {
          transaction.mutationStarted = true;
          this.#dispatchDomMutation(transaction, 'started');
        }
        if (!this.#isTransactionCurrent(transaction)) {
          render.abort();
          outcome = {
            status: 'superseded',
            html: prepared.html,
            source: prepared.source,
            requestURL: transaction.requestURL,
          };
          return Promise.resolve();
        }

        commitPromise = render
          .commit()
          .then((result) => {
            outcome = result;
            transaction.morphed = result.status === 'success';
            if (transaction.morphed) this.#dispatchDomMutation(transaction, 'morphed');
          })
          .catch((error) => {
            morphError = error;
          });
        return commitPromise;
      };

      if (useViewTransition) {
        await runProductGridTransition(
          transitionOwner,
          async () => {
            await startViewTransition(commit, ['product-grid'], transitionOwner);
            if (commitPromise) await commitPromise;
          }
        );
      } else {
        await commit();
      }

      if (morphError) throw morphError;

      if (
        !outcome ||
        outcome.status !== 'success' ||
        !this.#isTransactionCurrent(transaction)
      ) {
        this.#finishSupersededTransaction(transaction, outcome?.source ?? prepared.source);
        return;
      }

      this.#dispatchRenderSettlement(transaction, 'success', outcome.source);

      if (
        this.#isTransactionCurrent(transaction) &&
        transaction.publishHistory &&
        transaction.historyPolicy === 'write' &&
        !transaction.historyPublished
      ) {
        if (!this.#isTransactionCurrent(transaction)) return;
        history.pushState(history.state, '', transaction.historyURL);
        transaction.historyPublished = true;
      }

      settleSectionRenderAuthority(transaction.authority, 'success');
      transaction.transitionController.abort();
      this.#clearTransactionBusy(transaction);
      this.#captureCommittedUI();
      if (this.#activeRenderTransaction === transaction) this.#activeRenderTransaction = undefined;
    } catch (error) {
      render.abort();

      const isCurrent = this.#isTransactionCurrent(transaction);
      const isAbort =
        typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError';
      const status = !isCurrent || isAbort ? 'superseded' : 'failure';

      if (isCurrent) {
        this.#restoreCommittedUI();
        this.#clearTransactionBusy(transaction);
        if (this.#activeRenderTransaction === transaction) this.#activeRenderTransaction = undefined;
      }

      this.#dispatchRenderSettlement(transaction, status, render.source);
      settleSectionRenderAuthority(transaction.authority, status);
      transaction.transitionController.abort();

      if (isCurrent && !isAbort) {
        console.error(error);
      }
    }
  }

  /**
   * Publishes the exact speculative DOM-mutation phase to the section owner.
   * @param {FacetRenderTransaction} transaction
   * @param {'started' | 'morphed'} phase
   */
  #dispatchDomMutation(transaction, phase) {
    document.dispatchEvent(
      new CustomEvent(FACET_DOM_MUTATION_EVENT, {
        detail: {
          sectionId: transaction.sectionId,
          token: transaction.token,
          phase,
        },
      })
    );
  }

  /**
   * @param {FacetRenderTransaction} transaction
   * @param {'success' | 'failure' | 'superseded'} status
   * @param {'cache' | 'network'} source
   */
  #dispatchRenderSettlement(transaction, status, source) {
    if (this.#settledRenderTokens.has(transaction.token)) return;
    this.#settledRenderTokens.add(transaction.token);

    document.dispatchEvent(
      new CustomEvent(FACET_RENDER_SETTLED_EVENT, {
        detail: {
          ...transaction,
          status,
          source,
        },
      })
    );
    transaction.settlement = status;
  }

  /**
   * @param {FacetRenderTransaction} transaction
   * @param {'cache' | 'network'} source
   */
  #finishSupersededTransaction(transaction, source) {
    transaction.transitionController.abort();
    transaction.preparedRender?.abort();
    settleSectionRenderAuthority(transaction.authority, 'superseded');
    if (latestRenderTokens.get(transaction.sectionId) === transaction.token) {
      latestRenderTokens.delete(transaction.sectionId);
    }
    if (this.#activeRenderTransaction === transaction) {
      this.#restoreCommittedUI();
      this.#clearTransactionBusy(transaction);
      this.#activeRenderTransaction = undefined;
    }
    this.#dispatchRenderSettlement(transaction, 'superseded', source);
  }

  /** @param {CustomEvent} event */
  #handleRenderSupersede = (event) => {
    const detail = event.detail;
    const transaction = this.#activeRenderTransaction;
    if (
      !transaction ||
      typeof detail !== 'object' ||
      detail === null ||
      detail.sectionId !== transaction.sectionId ||
      detail.token !== transaction.token
    ) {
      return;
    }

    this.#finishSupersededTransaction(transaction, 'network');
  };

  /**
   * Marks only the current transaction as busy and remembers the committed value.
   *
   * @param {FacetRenderTransaction} transaction
   */
  #setTransactionBusy(transaction) {
    if (this.#activeBusyToken) {
      const form = this.refs.facetsForm;
      if (this.#committedAriaBusy === null) {
        form.removeAttribute('aria-busy');
      } else {
        form.setAttribute('aria-busy', this.#committedAriaBusy);
      }
    }

    const form = this.refs.facetsForm;
    this.#committedAriaBusy = form.getAttribute('aria-busy');
    this.#activeBusyToken = transaction.token;
    form.setAttribute('aria-busy', 'true');
  }

  /**
   * @param {FacetRenderTransaction} transaction
   */
  #clearTransactionBusy(transaction) {
    if (this.#activeBusyToken !== transaction.token || !this.isConnected) return;

    const form = this.refs.facetsForm;
    if (this.#committedAriaBusy === null) {
      form.removeAttribute('aria-busy');
    } else {
      form.setAttribute('aria-busy', this.#committedAriaBusy);
    }
    this.#activeBusyToken = undefined;
  }

  /**
   * Captures the last authoritative form and status state.
   */
  #captureCommittedUI() {
    const form = this.refs.facetsForm;
    if (!(form instanceof HTMLFormElement)) return;

    this.#committedControlState = Array.from(form.elements)
      .filter(
        (element) =>
          element instanceof HTMLInputElement ||
          element instanceof HTMLSelectElement ||
          element instanceof HTMLTextAreaElement
      )
      .map((element) => ({
        element,
        checked: element instanceof HTMLInputElement ? element.checked : undefined,
        value: element.value,
        disabled: element.disabled,
        selectedValues:
          element instanceof HTMLSelectElement && element.multiple
            ? Array.from(element.selectedOptions, (option) => option.value)
            : undefined,
      }));

    this.#committedStatusState = Array.from(
      this.querySelectorAll('facet-status-component, [ref="clearButton"]')
    ).map((element) => ({
      element: /** @type {HTMLElement} */ (element),
      html: element.innerHTML,
      className: element.className,
    }));
  }

  /**
   * Restores only the still-live DOM owned by the failed transaction.
   */
  #restoreCommittedUI() {
    if (!this.isConnected) return;

    for (const state of this.#committedControlState) {
      if (!state.element.isConnected || !this.contains(state.element)) continue;

      state.element.value = state.value;
      state.element.disabled = state.disabled;
      if (state.element instanceof HTMLInputElement && state.checked !== undefined) {
        state.element.checked = state.checked;
      }
      if (state.element instanceof HTMLSelectElement && state.selectedValues) {
        const selectedValues = new Set(state.selectedValues);
        for (const option of state.element.options) {
          option.selected = selectedValues.has(option.value);
        }
      }
    }

    for (const state of this.#committedStatusState) {
      if (!state.element.isConnected || !this.contains(state.element)) continue;
      state.element.innerHTML = state.html;
      state.element.className = state.className;
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    const historyURL = normalizeHistoryURL(this.#normalizeSearchURL(url));
    const requestURL = normalizeRequestURL(historyURL);
    const currentRequestURLKey = getPageCacheKey(window.location.href);
    const requestKey = getPageCacheKey(requestURL);
    const targetHistoryURL = historyURL.toString();
    const activeTransaction = this.#activeRenderTransaction;

    if (
      activeTransaction &&
      this.#canAdoptActiveTransaction(activeTransaction, requestKey, targetHistoryURL)
    ) {
      return;
    }
    if (activeTransaction) this.#finishSupersededTransaction(activeTransaction, 'network');

    if (
      requestKey === currentRequestURLKey &&
      this.#isCommittedFacetTarget(requestKey, targetHistoryURL)
    ) {
      if (historyURL.toString() !== normalizeHistoryURL(window.location.href).toString()) {
        history.pushState(history.state, '', historyURL.toString());
      }
      return;
    }

    this.#startFilterRender(new URLSearchParams(historyURL.search), historyURL, requestURL);
  }

  /**
   * @param {string} requestKey
   * @param {string} historyURL
   * @returns {boolean}
   */
  #isCommittedFacetTarget(requestKey, historyURL) {
    const section = this.closest('.shopify-section');
    const paginatedList = section?.querySelector('results-list, paginated-list');
    return Boolean(
      paginatedList &&
        'isFacetTargetCoherent' in paginatedList &&
        typeof paginatedList.isFacetTargetCoherent === 'function' &&
        paginatedList.isFacetTargetCoherent(requestKey, historyURL)
    );
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    void prefetchSection(this.sectionId, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
